import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";

interface ImageGalleryModalProps {
  images: Array<{ id: number; src: string; title: string }>;
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

export default function ImageGalleryModal({ 
  images, 
  currentIndex, 
  isOpen, 
  onClose 
}: ImageGalleryModalProps) {
  const [activeIndex, setActiveIndex] = useState(currentIndex);

  const nextImage = () => {
    setActiveIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative max-w-4xl w-full"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <motion.button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors hover-cursor"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <i className="fas fa-times"></i>
            </motion.button>

            {/* Main Image */}
            <motion.img
              key={activeIndex}
              src={images[activeIndex]?.src}
              alt={images[activeIndex]?.title}
              className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            />

            {/* Navigation Arrows */}
            <motion.button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors hover-cursor"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <i className="fas fa-chevron-left"></i>
            </motion.button>

            <motion.button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors hover-cursor"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <i className="fas fa-chevron-right"></i>
            </motion.button>

            {/* Image Info */}
            <div className="absolute bottom-4 left-4 text-white">
              <h3 className="text-xl font-semibold">{images[activeIndex]?.title}</h3>
              <p className="text-sm opacity-75">
                {activeIndex + 1} of {images.length}
              </p>
            </div>

            {/* Thumbnail Strip */}
            <div className="flex justify-center mt-4 space-x-2">
              {images.map((image, index) => (
                <motion.button
                  key={image.id}
                  onClick={() => setActiveIndex(index)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all hover-cursor ${
                    index === activeIndex ? 'border-white' : 'border-transparent opacity-60'
                  }`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <img
                    src={image.src}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                </motion.button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}